import { format } from 'date-fns'
import { useEffect, useRef } from 'react'
import { Bot, Info, Monitor } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { FrontendLog, LogLevel } from '@/types/frontendLog'
import AndroidIcon from '@/components/common/AndroidIcon'
import { enrichErrorMessage } from '@/common/errorDescriptions'
import {
  getScenarioLabelFromLog,
  getExecutionModeLabelFromLog,
} from '@/common/scenarioLabel'
import { getAppLabelFromLog } from '@/common/appLabel'

// plc_bridge などの自動実行ログ判定。ロールアップ等で platform が落ちても
// 判別できるよう、ノードが付与する context.automated で判定する。
function isAutomatedLog(log: FrontendLog): boolean {
  return log.context?.automated === true
}

function ForkliftIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="#f97316"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-label="Device"
    >
      <path d="M5 20h2m10 0h2" />
      <circle cx="7" cy="20" r="2" fill="#f97316" stroke="none" />
      <circle cx="17" cy="20" r="2" fill="#f97316" stroke="none" />
      <path d="M3 8v10h4M3 8l4-4h4v14" />
      <path d="M15 4v12h6V10l-3-6h-3z" />
    </svg>
  )
}

export const LEVEL_COLORS: Record<LogLevel, string> = {
  error: '#ef4444',
  warn: '#f97316',
  info: '#3b82f6',
  debug: '#6b7280',
}

export const LEVEL_LABELS: Record<LogLevel, string> = {
  error: 'ERROR',
  warn: 'WARN',
  info: 'INFO',
  debug: 'DEBUG',
}

interface LogTableProps {
  logs: FrontendLog[]
  onDetailClick: (log: FrontendLog) => void
  onRowClick?: (log: FrontendLog) => void
  showRobotId?: boolean
  siteNameMap?: Record<string, string>
  highlightScenarioStart?: boolean
  maxRows?: number
  compact?: boolean
  highlightedLogId?: string
  showAction?: boolean
  showCategory?: boolean
}

export default function LogTable({
  logs,
  onDetailClick,
  onRowClick,
  showRobotId = false,
  siteNameMap,
  highlightScenarioStart = false,
  maxRows,
  compact = false,
  highlightedLogId,
  showAction = true,
  showCategory = false,
}: LogTableProps) {
  const displayLogs = maxRows ? logs.slice(0, maxRows) : logs
  const highlightedRowRef = useRef<HTMLTableRowElement>(null)

  // ハイライトされたログにスクロール（ページ全体ではなく内側のスクロール領域のみ動かす）
  useEffect(() => {
    const row = highlightedRowRef.current
    if (!highlightedLogId || !row) return

    let container: HTMLElement | null = row.parentElement
    while (container && container !== document.body) {
      const overflowY = window.getComputedStyle(container).overflowY
      const scrollable =
        (overflowY === 'auto' || overflowY === 'scroll') &&
        container.scrollHeight > container.clientHeight
      if (scrollable) break
      container = container.parentElement
    }
    if (!container || container === document.body) return

    const containerRect = container.getBoundingClientRect()
    const rowRect = row.getBoundingClientRect()
    const offset =
      rowRect.top -
      containerRect.top -
      (containerRect.height - rowRect.height) / 2
    container.scrollTo({
      top: container.scrollTop + offset,
      behavior: 'smooth',
    })
  }, [highlightedLogId])

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    if (compact) {
      return format(date, 'HH:mm:ss')
    }
    const dayOfWeek = ['日', '月', '火', '水', '木', '金', '土'][date.getDay()]
    return `${date.toLocaleDateString('ja-JP')}(${dayOfWeek}) ${date.toLocaleTimeString('ja-JP')}`
  }

  const isScenarioStart = (log: FrontendLog) =>
    log.action === 'scenario_execute' && log.message === 'シナリオ実行開始'

  if (logs.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-500">
        ログがありません
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
              詳細
            </th>
            <th
              className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${compact ? 'w-20' : 'w-40'}`}
            >
              {compact ? '時刻' : '日時'}
            </th>
            {siteNameMap && (
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                サイト
              </th>
            )}
            {showRobotId && (
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                フォークID
              </th>
            )}
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
              レベル
            </th>
            <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
              PF
            </th>
            {showCategory && (
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                カテゴリ
              </th>
            )}
            {showAction && (
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                アクション
              </th>
            )}
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              メッセージ
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {displayLogs.map((log) => {
            const isHighlighted = highlightedLogId === log.log_id
            return (
              <tr
                key={log.log_id}
                ref={isHighlighted ? highlightedRowRef : undefined}
                onClick={() => onRowClick?.(log)}
                className={`
                ${onRowClick ? 'hover:bg-gray-100 cursor-pointer' : 'hover:bg-gray-50'}
                transition-colors
                ${highlightScenarioStart && isScenarioStart(log) ? 'bg-green-50 hover:bg-green-100' : ''}
                ${isHighlighted ? 'ring-2 ring-primary-500 ring-inset bg-primary-50' : ''}
              `}
              >
                <td className="px-2 py-2 text-center">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onDetailClick(log)
                    }}
                    className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-full transition-colors"
                    title="詳細を表示"
                  >
                    <Info className="w-4 h-4" />
                  </button>
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm">
                  <Link
                    to={`/forklifts/${log.robot_id}?timestamp=${encodeURIComponent(log.timestamp)}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-primary-600 hover:text-primary-800 hover:underline"
                  >
                    {formatTimestamp(log.timestamp)}
                  </Link>
                </td>
                {siteNameMap && (
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                    {siteNameMap[log.site_id] || log.site_id}
                  </td>
                )}
                {showRobotId && (
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                    {log.robot_id}
                  </td>
                )}
                <td className="px-4 py-2 whitespace-nowrap">
                  <span
                    className="px-2 py-1 rounded text-xs font-medium text-white"
                    style={{ backgroundColor: LEVEL_COLORS[log.level] }}
                  >
                    {LEVEL_LABELS[log.level]}
                  </span>
                </td>
                <td className="px-2 py-2 whitespace-nowrap">
                  <div className="flex items-center justify-center gap-1.5">
                    {log.platform === 'android' && (
                      <AndroidIcon className="w-4 h-4 flex-shrink-0 text-[#3DDC84]" />
                    )}
                    {log.platform === 'device' && (
                      <ForkliftIcon className="w-4 h-4 flex-shrink-0" />
                    )}
                    {isAutomatedLog(log) && (
                      <Bot
                        className="w-4 h-4 flex-shrink-0 text-emerald-600"
                        aria-label="自動実行"
                      />
                    )}
                    {!isAutomatedLog(log) &&
                      (!log.platform || log.platform === 'web') && (
                        <Monitor className="w-4 h-4 flex-shrink-0 text-blue-500" />
                      )}
                  </div>
                </td>
                {showCategory && (
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">
                    {log.category}
                  </td>
                )}
                {showAction && (
                  <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                    {log.action}
                    {(() => {
                      const appLabel = getAppLabelFromLog(log)
                      return appLabel ? (
                        <span className="ml-1.5 px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                          {appLabel}
                        </span>
                      ) : null
                    })()}
                  </td>
                )}
                <td className="px-4 py-2 text-sm text-gray-600 max-w-md truncate">
                  <span>
                    {enrichErrorMessage(
                      log.message,
                      log.context as Parameters<typeof enrichErrorMessage>[1]
                    )}
                  </span>
                  {(() => {
                    const scenarioLabel = getScenarioLabelFromLog(log)
                    return scenarioLabel ? (
                      <span className="ml-1 text-gray-700">
                        : {scenarioLabel}
                      </span>
                    ) : null
                  })()}
                  {(() => {
                    const modeLabel = getExecutionModeLabelFromLog(log)
                    return modeLabel ? (
                      <span className="ml-1.5 px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                        {modeLabel}
                      </span>
                    ) : null
                  })()}
                  {log.context?.duplicateCount != null &&
                    (log.context.duplicateCount as number) > 1 && (
                      <span className="ml-1.5 px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                        x{log.context.duplicateCount as number}
                      </span>
                    )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      {maxRows && logs.length > maxRows && (
        <p className="mt-2 text-sm text-gray-500 text-center">
          {logs.length} 件中 {maxRows} 件を表示
        </p>
      )}
    </div>
  )
}
